import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/client';
import { X, Plus, Trash2, Search } from 'lucide-react';

export default function DepartmentGLAssignments({ department, onClose }) {
  const [assignedGLs, setAssignedGLs] = useState([]);
  const [availableGLs, setAvailableGLs] = useState([]);
  const [selectedGLs, setSelectedGLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAvailableGLs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return availableGLs;
    return availableGLs.filter(gl =>
      String(gl.AccountCode || '').toLowerCase().includes(q) ||
      String(gl.AccountName || '').toLowerCase().includes(q) ||
      String(gl.AccountType || '').toLowerCase().includes(q)
    );
  }, [availableGLs, searchTerm]);

  useEffect(() => {
    if (department) {
      fetchAssignedGLs();
      fetchAvailableGLs();
    }
  }, [department]);

  const fetchAssignedGLs = async () => {
    try {
      const response = await apiClient.get(`/department-gl-accounts/${department.DepartmentID}/gl-accounts`);
      setAssignedGLs(response.data.glAccounts || response.data);
    } catch (error) {
      console.error('Failed to fetch assigned GL accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableGLs = async () => {
    try {
      const response = await apiClient.get(`/department-gl-accounts/${department.DepartmentID}/gl-accounts/available`);
      setAvailableGLs(response.data.glAccounts || response.data);
    } catch (error) {
      console.error('Failed to fetch available GL accounts:', error);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedGLs.length === 0) {
      alert('Please select at least one GL account');
      return;
    }

    try {
      const response = await apiClient.post(`/department-gl-accounts/${department.DepartmentID}/gl-accounts/bulk`, {
        glAccountIds: selectedGLs
      });

      const result = response.data;
      alert(`Assigned ${result.assigned} GL accounts\n${result.skipped > 0 ? `Skipped ${result.skipped} (already assigned)` : ''}`);
      
      setSelectedGLs([]);
      setShowBulkAdd(false);
      fetchAssignedGLs();
      fetchAvailableGLs();
    } catch (error) {
      console.error('Bulk assign error:', error);
      alert('Failed to assign GL accounts');
    }
  };

  const handleUnassign = async (glAccountId, accountCode) => {
    if (!confirm(`Unassign GL account ${accountCode} from this department?`)) return;

    try {
      await apiClient.delete(`/department-gl-accounts/${department.DepartmentID}/gl-accounts/${glAccountId}`);
      fetchAssignedGLs();
      fetchAvailableGLs();
    } catch (error) {
      console.error('Unassign error:', error);
      alert('Failed to unassign GL account');
    }
  };

  const toggleGLSelection = (glAccountId) => {
    setSelectedGLs(prev =>
      prev.includes(glAccountId)
        ? prev.filter(id => id !== glAccountId)
        : [...prev, glAccountId]
    );
  };

  const selectAllByType = (accountType) => {
    const typeGLs = availableGLs
      .filter(gl => gl.AccountType === accountType)
      .map(gl => gl.GLAccountID);
    setSelectedGLs(prev => [...new Set([...prev, ...typeGLs])]);
  };

  const selectAllFiltered = () => {
    const ids = filteredAvailableGLs.map(gl => gl.GLAccountID);
    setSelectedGLs(prev => [...new Set([...prev, ...ids])]);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage GL Accounts</h2>
            <p className="text-sm text-gray-600 mt-1">{department.DepartmentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Assigned GL Accounts */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Assigned GL Accounts ({assignedGLs.length})
              </h3>
              <button
                onClick={() => setShowBulkAdd(!showBulkAdd)}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add GL Accounts
              </button>
            </div>

            {assignedGLs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                No GL accounts assigned yet. Click "Add GL Accounts" to assign.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {assignedGLs.map(gl => (
                      <tr key={gl.GLAccountID} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{gl.AccountCode}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{gl.AccountName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{gl.AccountType}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleUnassign(gl.GLAccountID, gl.AccountCode)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bulk Add Section */}
          {showBulkAdd && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select GL Accounts to Assign ({selectedGLs.length} selected)
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedGLs([])}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>

              {/* Quick Select by Type */}
              <div className="mb-3 flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-700">Quick select:</span>
                {['Asset', 'Liability', 'Equity', 'Revenue', 'Expense', 'CostOfSales'].map(type => (
                  <button
                    key={type}
                    onClick={() => selectAllByType(type)}
                    className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                  >
                    All {type}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="mb-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by code, name or type..."
                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-600 whitespace-nowrap">
                  Showing {filteredAvailableGLs.length} of {availableGLs.length}
                </div>
                {searchTerm && filteredAvailableGLs.length > 0 && (
                  <button
                    type="button"
                    onClick={selectAllFiltered}
                    className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 whitespace-nowrap"
                  >
                    Select all matches
                  </button>
                )}
              </div>

              {/* Available GL Accounts */}
              <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg bg-white">
                {availableGLs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    All GL accounts are already assigned
                  </div>
                ) : filteredAvailableGLs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No accounts match "{searchTerm}"
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">
                          <input
                            type="checkbox"
                            checked={filteredAvailableGLs.length > 0 && filteredAvailableGLs.every(gl => selectedGLs.includes(gl.GLAccountID))}
                            onChange={(e) => {
                              const ids = filteredAvailableGLs.map(gl => gl.GLAccountID);
                              if (e.target.checked) {
                                setSelectedGLs(prev => [...new Set([...prev, ...ids])]);
                              } else {
                                setSelectedGLs(prev => prev.filter(id => !ids.includes(id)));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredAvailableGLs.map(gl => (
                        <tr
                          key={gl.GLAccountID}
                          className={`hover:bg-gray-50 cursor-pointer ${selectedGLs.includes(gl.GLAccountID) ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleGLSelection(gl.GLAccountID)}
                        >
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedGLs.includes(gl.GLAccountID)}
                              onChange={() => toggleGLSelection(gl.GLAccountID)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{gl.AccountCode}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{gl.AccountName}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{gl.AccountType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkAdd(false);
                    setSelectedGLs([]);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkAssign}
                  disabled={selectedGLs.length === 0}
                  className="btn btn-primary"
                >
                  Assign {selectedGLs.length} Account{selectedGLs.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
